import { useEffect, useState } from 'react';
import { ArrowLeft, BadgeCheck, CreditCard, KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import supabase from '@/supabase';
import type { Database } from '../../../database.types';
import { getTierColor, getTierLabel } from '@/types/entitlements';
import { cn } from '@/lib/utils';
import {
  DEFAULT_ADMIN_RUNTIME_CONFIG,
  normalizeAdminRuntimeConfig,
  type AdminRuntimeConfig,
} from '@/lib/admin-runtime-config';

type Purchase = Database['public']['Tables']['purchases']['Row'];
type Subscription = Database['public']['Tables']['subscriptions']['Row'];
type Entitlement = Database['public']['Tables']['entitlements']['Row'];

type AdminListUser = {
  id: string;
  email: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
  app_role: string;
  membership_status: string;
  membership_plan: string;
  credits_remaining: number;
};

type AdminDetailUser = {
  id: string;
  email: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  profile: {
    id: string;
    username: string | null;
    display_name: string | null;
    avatar_url: string | null;
    description: string | null;
    app_role: string | null;
  } | null;
  subscription: Subscription | null;
  entitlement: Entitlement | null;
  purchases: Purchase[];
};

interface AdminActionResponse {
  success?: boolean;
  message?: string;
  action_link?: string | null;
}

function initialsFor(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function formatDate(value: string | null): string {
  if (!value) return 'N/A';
  return new Date(value).toLocaleString();
}

function getStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'active':
    case 'completed':
      return 'default';
    case 'trialing':
      return 'secondary';
    case 'unsubscribed':
      return 'outline';
    case 'processing':
    case 'expired':
    case 'failed':
    case 'canceled':
    case 'refunded':
      return 'destructive';
    default:
      return 'outline';
  }
}

function RolePlanBadge({ appRole, plan }: { appRole: string | null | undefined; plan: string | null | undefined }) {
  const isAdmin = appRole === 'admin';
  const label = isAdmin ? 'Admin' : getTierLabel(plan || 'free');
  const colorClass = isAdmin ? 'bg-red-100 text-red-700' : getTierColor(plan || 'free');

  return (
    <Badge
      variant="secondary"
      className={cn('min-h-0 rounded-full px-1.75 py-0.5 text-[0.6rem] font-semibold leading-none', colorClass)}
    >
      {label}
    </Badge>
  );
}

interface AdminUserManagementProps {
  adminSettings: Partial<AdminRuntimeConfig> | null;
  onSaveAdminSettings: (settings: Partial<AdminRuntimeConfig>) => Promise<void>;
}

export default function AdminUserManagement({
  adminSettings,
  onSaveAdminSettings,
}: AdminUserManagementProps) {
  const [users, setUsers] = useState<AdminListUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<AdminDetailUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isActing, setIsActing] = useState(false);
  const [isSavingRuntimeConfig, setIsSavingRuntimeConfig] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [runtimeConfig, setRuntimeConfig] = useState<AdminRuntimeConfig>(
    normalizeAdminRuntimeConfig(adminSettings)
  );

  useEffect(() => {
    setRuntimeConfig(normalizeAdminRuntimeConfig(adminSettings));
  }, [adminSettings]);

  const invokeAdminAction = async <T,>(body: object): Promise<T> => {
    const { data, error: invokeError } = await supabase.functions.invoke('admin-manage-users', {
      body,
    });

    if (invokeError) throw invokeError;
    return data as T;
  };

  const loadUsers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await invokeAdminAction<{ success: boolean; users: AdminListUser[] }>({
        action: 'list_users',
      });
      setUsers(response.users || []);
    } catch (loadError) {
      console.error('Failed to load admin users:', loadError);
      setError(loadError instanceof Error ? loadError.message : 'Failed to load users.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserDetail = async (userId: string) => {
    setIsDetailLoading(true);
    setError(null);
    setStatusMessage(null);

    try {
      const response = await invokeAdminAction<{ success: boolean; user: AdminDetailUser }>({
        action: 'get_user',
        user_id: userId,
      });
      setSelectedUser(response.user);
    } catch (loadError) {
      console.error('Failed to load admin user detail:', loadError);
      setError(loadError instanceof Error ? loadError.message : 'Failed to load user detail.');
    } finally {
      setIsDetailLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
  }, []);

  const handleSaveRuntimeConfig = async () => {
    setIsSavingRuntimeConfig(true);
    setError(null);
    setStatusMessage(null);

    try {
      const nextConfig = normalizeAdminRuntimeConfig(runtimeConfig);
      await onSaveAdminSettings(nextConfig);
      setRuntimeConfig(nextConfig);
      setStatusMessage('Admin runtime URLs updated.');
    } catch (saveError) {
      console.error('Failed to save admin runtime config:', saveError);
      setError(saveError instanceof Error ? saveError.message : 'Failed to save admin runtime URLs.');
    } finally {
      setIsSavingRuntimeConfig(false);
    }
  };

  const handleAdminAction = async (
    action: 'reset_password' | 'set_user_role',
    extraBody?: Record<string, unknown>
  ) => {
    if (!selectedUser) return;

    setIsActing(true);
    setError(null);
    setStatusMessage(null);

    try {
      const response = await invokeAdminAction<AdminActionResponse>({
        action,
        user_id: selectedUser.id,
        ...extraBody,
      });

      if (action === 'reset_password' && response.action_link) {
        setStatusMessage(`Recovery link generated: ${response.action_link}`);
      } else {
        setStatusMessage(response.message || 'Action completed.');
      }

      await loadUserDetail(selectedUser.id);
      await loadUsers();
    } catch (actionError) {
      console.error('Admin user action failed:', actionError);
      setError(actionError instanceof Error ? actionError.message : 'Action failed.');
    } finally {
      setIsActing(false);
    }
  };

  if (selectedUser) {
    const displayName = selectedUser.profile?.display_name || selectedUser.profile?.username || selectedUser.email || 'Unknown user';
    const plan = selectedUser.subscription?.plan || selectedUser.entitlement?.tier || 'free';
    const membershipStatus = selectedUser.subscription?.status || 'unsubscribed';

    return (
      <div className="mt-4 space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setSelectedUser(null)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h3 className="text-lg font-semibold">{displayName}</h3>
            <p className="text-sm text-muted-foreground">{selectedUser.email || 'No email'}</p>
          </div>
        </div>

        {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        {statusMessage && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 break-all">{statusMessage}</div>}

        {isDetailLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <Card>
              <CardContent className="flex items-center gap-4 pt-6">
                <Avatar className="h-14 w-14">
                  <AvatarImage src={selectedUser.profile?.avatar_url || undefined} alt={displayName} />
                  <AvatarFallback>{initialsFor(displayName)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-lg font-semibold">{displayName}</p>
                  <p className="truncate text-sm text-muted-foreground">{selectedUser.email || 'No email address'}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <RolePlanBadge appRole={selectedUser.profile?.app_role} plan={plan} />
                    <Badge variant={getStatusVariant(membershipStatus)}>
                      {membershipStatus}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <BadgeCheck className="h-4 w-4" />
                  Current Membership Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p>Status: <span className="font-medium">{membershipStatus}</span></p>
                <p>Plan: <span className="font-medium">{getTierLabel(plan)}</span></p>
                <p>Credits remaining: <span className="font-medium">{selectedUser.entitlement?.credits_remaining ?? 0}</span></p>
                <p>Current period end: <span className="font-medium">{formatDate(selectedUser.subscription?.current_period_end || null)}</span></p>
                <p>Last sign in: <span className="font-medium">{formatDate(selectedUser.last_sign_in_at)}</span></p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <ShieldCheck className="h-4 w-4" />
                  Admin Actions
                </CardTitle>
                <CardDescription>Generate a reset link or change this user’s app role.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-2">
                <Button variant="outline" disabled={isActing} onClick={() => void handleAdminAction('reset_password')}>
                  {isActing ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                  Reset Password
                </Button>
                <Button
                  variant={selectedUser.profile?.app_role === 'admin' ? 'outline' : 'default'}
                  disabled={isActing}
                  onClick={() =>
                    void handleAdminAction('set_user_role', {
                      app_role: selectedUser.profile?.app_role === 'admin' ? 'user' : 'admin',
                    })
                  }
                >
                  {isActing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                  {selectedUser.profile?.app_role === 'admin' ? 'Remove Admin' : 'Promote To Admin'}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <CreditCard className="h-4 w-4" />
                  Purchase History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedUser.purchases.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No purchases found for this user.</p>
                ) : (
                  <div className="space-y-3">
                    {selectedUser.purchases.map((purchase) => (
                      <div key={purchase.id} className="flex items-center justify-between border-b py-2 last:border-b-0">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{purchase.sku}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(purchase.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{(purchase.amount / 100).toLocaleString('en-US', { style: 'currency', currency: purchase.currency || 'USD' })}</span>
                          <Badge variant={getStatusVariant(purchase.status)}>{purchase.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {statusMessage && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{statusMessage}</div>}

      <Card>
        <CardHeader>
          <CardTitle>Generation Endpoints</CardTitle>
          <CardDescription>Override the provider targets used behind the AI gateway for admin sessions. The app still calls the gateway; these values repoint what the gateway-facing requests should use.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="admin-chat-generation-url">Chat Generation</Label>
            <Input
              id="admin-chat-generation-url"
              value={runtimeConfig.chat_generation_url}
              onChange={(event) =>
                setRuntimeConfig((prev) => ({ ...prev, chat_generation_url: event.target.value }))
              }
              placeholder={DEFAULT_ADMIN_RUNTIME_CONFIG.chat_generation_url}
            />
            <p className="text-xs text-muted-foreground">Default: Lightspeed chat endpoint. Example override: your Ollama tunnel chat URL.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-media-generation-url">Media Generation</Label>
            <Input
              id="admin-media-generation-url"
              value={runtimeConfig.media_generation_url}
              onChange={(event) =>
                setRuntimeConfig((prev) => ({ ...prev, media_generation_url: event.target.value }))
              }
              placeholder={DEFAULT_ADMIN_RUNTIME_CONFIG.media_generation_url}
            />
            <p className="text-xs text-muted-foreground">Default: Lightspeed media base URL used for image and video generation.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-media-location-url">Media Location</Label>
            <Input
              id="admin-media-location-url"
              value={runtimeConfig.media_location_url}
              onChange={(event) =>
                setRuntimeConfig((prev) => ({ ...prev, media_location_url: event.target.value }))
              }
              placeholder={DEFAULT_ADMIN_RUNTIME_CONFIG.media_location_url}
            />
            <p className="text-xs text-muted-foreground">Base URL used to resolve relative media URLs returned by the provider.</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => void handleSaveRuntimeConfig()} disabled={isSavingRuntimeConfig}>
              {isSavingRuntimeConfig ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save URLs
            </Button>
            <Button
              variant="outline"
              onClick={() => setRuntimeConfig(DEFAULT_ADMIN_RUNTIME_CONFIG)}
              disabled={isSavingRuntimeConfig}
            >
              Reset Defaults
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Manage Users</CardTitle>
          <CardDescription>Inspect users, review memberships, and run admin account actions.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-sm text-muted-foreground">No users found.</p>
          ) : (
            users.map((user) => {
              const displayName = user.display_name || user.username || user.email || 'Unknown user';

              return (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => void loadUserDetail(user.id)}
                  className="flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-accent"
                >
                  <Avatar className="h-11 w-11">
                    <AvatarImage src={user.avatar_url || undefined} alt={displayName} />
                    <AvatarFallback>{initialsFor(displayName)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{displayName}</p>
                    <p className="truncate text-xs text-muted-foreground">{user.email || 'No email'}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge variant={getStatusVariant(user.membership_status)}>{user.membership_status}</Badge>
                    <RolePlanBadge appRole={user.app_role} plan={user.membership_plan} />
                  </div>
                </button>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}
